export const GamificationChallengeEvent = {
  MEMBER_GET_MEMBER: 'member_get_member',
} as const;

export type GamificationChallengeEventType =
  (typeof GamificationChallengeEvent)[keyof typeof GamificationChallengeEvent];

export const GamificationChallengeVariant = {
  STANDARD: 'standard',
  REFERRAL: 'referral',
} as const;

export type GamificationChallengeVariantType =
  (typeof GamificationChallengeVariant)[keyof typeof GamificationChallengeVariant];

export const isReferralEvent = (event?: string): boolean =>
  event === GamificationChallengeEvent.MEMBER_GET_MEMBER;

export const GamificationChallengeClass = {
  ONBOARDING: 'onboarding',
  SEASON: 'season',
} as const;

export type GamificationChallengeClassType =
  (typeof GamificationChallengeClass)[keyof typeof GamificationChallengeClass];

export type OnboardingChecklistItem = {
  id: string | number;
  name: string;
  description?: string;
  points?: number;
  event?: string;
  user_status?: string;
  display_order?: number;
  isDone: boolean;
};

export type OnboardingChecklist = {
  isOnboardingMode: boolean;
  items: OnboardingChecklistItem[];
  completedCount: number;
  totalCount: number;
};

const isFinished = (challenge: { user_status?: string }): boolean =>
  challenge.user_status === 'finished';

export const isOnboardingChallenge = (challenge: {
  challenge_class?: string;
}): boolean => challenge.challenge_class === GamificationChallengeClass.ONBOARDING;

/**
 * Derives the onboarding checklist from the challenge list itself — the API
 * already hides onboarding challenges outside the user's validity window, so an
 * empty result is what takes the dropdown back to season challenges.
 */
export const selectOnboardingChecklist = (
  challenges: Array<Record<string, any>>,
): OnboardingChecklist => {
  const onboardingChallenges = challenges.filter(isOnboardingChallenge);

  const items = [...onboardingChallenges]
    .sort((a, b) => {
      // Pending first, finished last (Figma: concluded tasks sink to the bottom).
      const doneDiff = Number(isFinished(a)) - Number(isFinished(b));
      if (doneDiff !== 0) {
        return doneDiff;
      }

      return (a.display_order ?? 0) - (b.display_order ?? 0);
    })
    .map(challenge => ({ ...challenge, isDone: isFinished(challenge) })) as OnboardingChecklistItem[];

  const completedCount = items.filter(item => item.isDone).length;

  return {
    isOnboardingMode: items.length > 0 && completedCount < items.length,
    items,
    completedCount,
    totalCount: items.length,
  };
};
